import { register } from "./registry";
import { toast } from "./toast";

register((root) => {
  const choice = root.querySelector<HTMLInputElement>("[data-bilti-vat]");
  const form = choice?.form;
  if (!choice || !form || form.dataset.biltiVatBound) return;
  form.dataset.biltiVatBound = "1";
  const payer = () => form.querySelector<HTMLInputElement>("[name='Form.BillToLedgerId']")?.value ?? "";
  let lastPayer = payer();
  let sequence = 0;
  let pending: Promise<void> | null = null;
  let failed = false;
  let waitingForSave = false;
  const payerRequests = new Map<XMLHttpRequest, { done: Promise<void>; finish: () => void }>();
  // Checkbox.value stays "true" when unchecked, so htmx's input changed filter
  // cannot detect subsequent manual or remembered VAT choices.
  const refresh = () => choice.dispatchEvent(new CustomEvent("kit:vat-change", { bubbles: true }));
  const load = () => {
    if (!form.isConnected || choice.disabled) return;
    const id = payer();
    if (id === lastPayer) return;
    lastPayer = id;
    const mine = ++sequence;
    failed = false;
    choice.checked = false;
    refresh();
    if (!id) return;
    const request = fetch(`/Operations/Bilti/Entry?handler=Vat&ledgerId=${encodeURIComponent(id)}`)
      .then(async response => {
        if (!response.ok) throw new Error("VAT choice lookup failed");
        const result = await response.json() as { applied: boolean };
        if (mine !== sequence || id !== payer()) return;
        choice.checked = result.applied;
        refresh();
      })
      .catch(() => {
        if (mine !== sequence) return;
        failed = true;
        toast("error", "Could not load this payer's VAT choice. Choose Apply VAT before saving.");
      })
      .finally(() => { if (pending === request) pending = null; });
    pending = request;
  };
  choice.addEventListener("change", () => { sequence++; failed = false; refresh(); }); // a manual choice wins over an in-flight default
  form.addEventListener("change", load);
  form.addEventListener("input", (e) => {
    if ((e.target as HTMLElement).dataset.kitAcTarget === "Form.BillToLedgerId") load();
  });
  document.body.addEventListener("htmx:afterSwap", load); // the default Bill To is replaced when pay mode/parties change
  form.addEventListener("htmx:beforeRequest", (e) => {
    const detail = (e as CustomEvent<{ target: Element; xhr: XMLHttpRequest }>).detail;
    if (detail.target?.id !== "bill-to-field") return;
    let finish!: () => void;
    const done = new Promise<void>(resolve => { finish = resolve; });
    payerRequests.set(detail.xhr, { done, finish });
  });
  form.addEventListener("htmx:afterRequest", (e) => {
    const detail = (e as CustomEvent<{ xhr: XMLHttpRequest; successful: boolean }>).detail;
    const request = payerRequests.get(detail.xhr);
    if (!request) return;
    payerRequests.delete(detail.xhr);
    if (!detail.successful) {
      failed = true;
      toast("error", "Could not load Bill To. Select the payer before saving.");
    }
    request.finish();
  });
  form.addEventListener("submit", (e) => {
    if (!payerRequests.size && !pending && !failed) return;
    e.preventDefault(); e.stopImmediatePropagation();
    if (failed || waitingForSave) return;
    waitingForSave = true;
    const submitter = (e as SubmitEvent).submitter as HTMLButtonElement | null;
    void (async () => {
      while (payerRequests.size || pending) {
        await Promise.all([...Array.from(payerRequests.values(), request => request.done), ...(pending ? [pending] : [])]);
      }
      waitingForSave = false;
      if (!failed && form.isConnected) form.requestSubmit(submitter);
    })();
  }, true);
  form.addEventListener("keydown", (e) => {
    if (!e.altKey || e.key.toLowerCase() !== "e" || choice.disabled || choice.closest("fieldset[disabled]")) return;
    e.preventDefault();
    choice.checked = !choice.checked;
    choice.dispatchEvent(new Event("change", { bubbles: true }));
  });
  form.addEventListener("kit:saved", () => { sequence++; lastPayer = ""; choice.checked = false; failed = false; });
});
